import { motion } from "framer-motion";
import { Sparkles } from "lucide-react";
import { aboutPageData } from "../../data/aboutData";
import ActionLink from "../common/ActionLink";

const reveal = {
  hidden: { opacity: 0, y: 24 },
  visible: (delay = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay, duration: 0.68, ease: [0.16, 1, 0.3, 1] }
  })
};

function AboutHero() {
  const { hero, cta } = aboutPageData;

  return (
    <section className="relative overflow-hidden pb-16 pt-28 md:pb-24 md:pt-36">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-600 via-amber-700 to-amber-950" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_14%_9%,rgba(255,255,255,0.15),transparent_40%),radial-gradient(circle_at_84%_14%,rgba(247,179,43,0.2),transparent_40%)]" />
        <div className="absolute inset-0" style={{ backgroundImage: 'repeating-linear-gradient(45deg, rgba(255,255,255,0.05) 0, rgba(255,255,255,0.05) 1px, transparent 1px, transparent 24px)' }} />
        <div className="absolute -left-16 top-16 h-56 w-56 rounded-full bg-warm-gold/20 blur-[100px]" />
        <div className="absolute right-0 top-24 h-64 w-64 rounded-full bg-white/10 blur-[100px]" />

        <div className="relative mx-auto w-full max-w-7xl px-6">
          <div className="grid gap-12 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
            <div>
              <motion.p
                custom={0.06}
                variants={reveal}
                initial="hidden"
                animate="visible"
                className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.16em] text-white shadow-sm"
              >
                <Sparkles className="h-3.5 w-3.5 text-amber-300" aria-hidden="true" />
                {hero.kicker}
              </motion.p>

              <motion.h1
                custom={0.14}
                variants={reveal}
                initial="hidden"
                animate="visible"
                className="mt-6 max-w-3xl text-3xl leading-[1.08] font-bold tracking-tight text-white sm:text-4xl md:text-6xl drop-shadow-md"
              >
                {hero.title}
              </motion.h1>

              <motion.p
                custom={0.22}
                variants={reveal}
                initial="hidden"
                animate="visible"
                className="mt-6 max-w-2xl text-sm leading-relaxed text-amber-50/90 sm:text-base md:text-lg"
              >
                {hero.subtitle}
              </motion.p>

              <motion.div
                custom={0.26}
                variants={reveal}
                initial="hidden"
                animate="visible"
                className="mt-6 flex flex-wrap gap-2"
              >
                {hero.trustTags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-full border border-white/20 bg-amber-900/40 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.08em] text-white backdrop-blur-sm"
                  >
                    {tag}
                  </span>
                ))}
              </motion.div>

              <motion.div
                custom={0.3}
                variants={reveal}
                initial="hidden"
                animate="visible"
                className="mt-10 flex flex-col gap-4 sm:flex-row"
              >
                <div className="inline-flex">
                  <ActionLink variant="ctaPrimary" to={cta.primary.to} label={cta.primary.label} />
                </div>
                <div className="inline-flex text-white hover:text-white">
                  <ActionLink
                    variant="ctaSecondary"
                    to="/study-in/usa"
                    label="Explore Destinations"
                    showArrow={false}
                  />
                </div>
              </motion.div>

              <motion.div
                custom={0.34}
                variants={reveal}
                initial="hidden"
                animate="visible"
                className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-3 pt-8 border-t border-white/15"
              >
                {hero.metrics.map((metric) => (
                  <div key={metric.label}>
                    <p className="text-xl font-bold tracking-tight text-white sm:text-2xl">{metric.value}</p>
                    <p className="mt-1 text-xs font-semibold tracking-wider uppercase text-amber-200/80">{metric.label}</p>
                  </div>
                ))}
              </motion.div>
            </div>

            <motion.div
              custom={0.28}
              variants={reveal}
              initial="hidden"
              animate="visible"
              className="relative max-w-sm mx-auto w-full"
            >
              <div className="rounded-[2rem] border border-white/20 bg-amber-900/40 p-8 md:p-10 shadow-luxury backdrop-blur-xl text-center">
                <div className="mx-auto w-12 h-12 flex items-center justify-center rounded-xl bg-amber-500/20 border border-amber-400/30 mb-6">
                  <span className="text-2xl">💡</span>
                </div>
                <p className="text-lg font-medium leading-relaxed text-white italic drop-shadow-sm">
                  "Our mission isn't just about sending students abroad; it's about making sure they thrive once they get there."
                </p>
                <div className="mt-8 pt-6 border-t border-white/20">
                  <p className="text-sm font-bold uppercase tracking-widest text-amber-200/90">The NextEra Team</p>
                </div>
              </div>
            </motion.div>
          </div>
      </div>
    </section>
  );
}

export default AboutHero;
